// frontend/src/pages/perfis/PerfilPermissionsPage.jsx
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionMatrix from '../../components/rbac/PermissionMatrix';

function PerfilPermissionsPage() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { id: perfilId } = useParams();

  // Permissão para acessar esta página
  const canManage = hasPermission('config.perfis');

  const [perfil, setPerfil] = useState(null);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!canManage) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const [perfilResponse, permissionsResponse] = await Promise.all([
          api.get(`/perfis/${perfilId}/permissions`),
          api.get('/permissions'),
        ]);

        const perfilData = perfilResponse.data?.data ?? perfilResponse.data;
        const allPermsData = permissionsResponse.data?.data ?? permissionsResponse.data;

        setPerfil(perfilData);
        setSelectedPermissions(perfilData.permissions || []);
        setAllPermissions(allPermsData);
      } catch (err) {
        setError('Falha ao carregar os dados de permissões. Tente novamente.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [perfilId, canManage]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/perfis/${perfilId}/permissions`, {
        permissions: selectedPermissions,
      });
      alert('Permissões salvas com sucesso!');
      navigate('/painel/perfis');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Ocorreu um erro ao salvar.';
      setError(errorMessage);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="perfis-permissions-page">
        <LoadingSpinner text="Carregando permissões..." />
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="perfis-permissions-page">
        <div className="alert alert-danger">
          <h4>Acesso Negado</h4>
          <p>Você não tem permissão para gerenciar perfis e permissões.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="perfis-permissions-page">
      <div className="content-header">
        <h1 className="content-title">Gerenciar Permissões</h1>
        <p className="content-subtitle">
          Atribuindo permissões para o perfil: <strong>{perfil?.nome}</strong>
        </p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card perfis-permissions-card">
        <div className="card-body">
          <PermissionMatrix
            allPermissions={allPermissions}
            selectedPermissions={selectedPermissions}
            onChange={setSelectedPermissions}
          />
        </div>
        <div className="card-footer perfis-permissions-footer text-end">
          <button
            className="btn btn-secondary me-2"
            onClick={() => navigate('/painel/perfis')}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="btn btn-success"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PerfilPermissionsPage;
